import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSafeAuth as useAuth } from '../../hooks/useSafeAuth';

// Props interface for the ProtectedRoute component
// children: The React components that should only be shown to authenticated users
interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute Component
 * 
 * This component acts as a security guard for pages that require user authentication.
 * It checks if a user is logged in before showing the requested page.
 * 
 * How it works:
 * 1. Checks if the user authentication is still loading
 * 2. If loading, shows a spinning loading animation
 * 3. If not logged in, redirects user to login page
 * 4. If logged in, shows the protected content (children)
 * 
 * @param children - The page content that should only be visible to logged-in users
 */
function ProtectedRoute({ children }: ProtectedRouteProps) {
  // Get the current user and loading state from the authentication system
  const { user, loading } = useAuth();
  
  // Get the current page location so we can redirect back here after login
  const location = useLocation();

  // If we're still checking if the user is logged in, show a loading spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          {/* Spinning circle animation to indicate loading */}
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user is logged in, redirect them to the login page
  if (!user) {
    // Save the current location so after login, we can bring them back to where they wanted to go
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // User is authenticated, so show them the protected content
  return <>{children}</>;
}

export default ProtectedRoute;